import React from 'react';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Shield, Globe, CheckCircle, GraduationCap, Briefcase, Stethoscope } from 'lucide-react';

const Healthcare = () => {
    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-950">
            <Navbar />
            <main className="pt-24 pb-16">
                {/* Hero Section */}
                <section className="py-20 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-red-500/10 to-pink-500/10 blur-3xl" />
                    <div className="container mx-auto px-4 relative z-10">
                        <div className="max-w-4xl mx-auto text-center">
                            <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-full px-4 py-2 mb-6">
                                <Heart className="w-5 h-5 text-red-400" />
                                <span className="text-red-300 font-medium text-sm">Healthcare Careers in Germany</span>
                            </div>
                            <h1 className="text-5xl md:text-7xl font-black text-white mb-6">
                                Nurse <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-pink-500">Recruitment</span>
                            </h1>
                            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                                Germany is actively seeking qualified nursing professionals (Pflegefachkräfte). We guide you through the entire journey—from language training and qualification recognition (Anerkennung) to securing a top-tier hospital placement.
                            </p>
                            <Link
                                to="/signup"
                                className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-pink-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-[0_0_30px_rgba(239,68,68,0.5)] transition-all"
                            >
                                Apply Now
                                <ArrowRight className="w-5 h-5" />
                            </Link>
                        </div>
                    </div>
                </section>

                {/* Eligibility & Requirements */}
                <section className="py-16">
                    <div className="container mx-auto px-4">
                        <h2 className="text-3xl md:text-5xl font-black text-white text-center mb-12">
                            Eligibility & Requirements
                        </h2>
                        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                            <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700 rounded-2xl p-8 hover:border-red-500/50 transition-all">
                                <GraduationCap className="w-10 h-10 text-red-400 mb-6" />
                                <h3 className="text-2xl font-bold text-white mb-4">Qualifications</h3>
                                <ul className="space-y-3">
                                    <li className="flex items-start gap-3 text-slate-300">
                                        <CheckCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                                        <span>BSc Nursing or GNM (General Nursing and Midwifery) Diploma</span>
                                    </li>
                                    <li className="flex items-start gap-3 text-slate-300">
                                        <CheckCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                                        <span>Valid Nursing Registration License from your home country</span>
                                    </li>
                                    <li className="flex items-start gap-3 text-slate-300">
                                        <CheckCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                                        <span>Minimum 1-2 years of clinical experience (preferred)</span>
                                    </li>
                                </ul>
                            </div>
                            <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700 rounded-2xl p-8 hover:border-red-500/50 transition-all">
                                <Globe className="w-10 h-10 text-red-400 mb-6" />
                                <h3 className="text-2xl font-bold text-white mb-4">Language & Licensing</h3>
                                <ul className="space-y-3">
                                    <li className="flex items-start gap-3 text-slate-300">
                                        <CheckCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                                        <span>B1 or B2 level German language certification (Goethe, ÖSD, TELC)</span>
                                    </li>
                                    <li className="flex items-start gap-3 text-slate-300">
                                        <CheckCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                                        <span>Defizitbescheid (Deficit Notice) for the Anerkennung process</span>
                                    </li>
                                    <li className="flex items-start gap-3 text-slate-300">
                                        <CheckCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                                        <span>Willingness to complete the Adaptation Course (Anpassungslehrgang) or Knowledge Test (Kenntnisprüfung) in Germany</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Benefits Section */}
                <section className="py-16 bg-slate-800/30">
                    <div className="container mx-auto px-4">
                        <h2 className="text-3xl md:text-5xl font-black text-white text-center mb-12">
                            Why Choose Germany?
                        </h2>
                        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                            {[
                                { icon: Briefcase, title: 'Excellent Salary', desc: 'Starting salaries between €2,900 - €3,500 gross per month, plus shift allowances and bonuses.' },
                                { icon: Shield, title: 'Job Security & Benefits', desc: 'Permanent employment contracts, comprehensive healthcare, and generous pension schemes.' },
                                { icon: Heart, title: 'Work-Life Balance', desc: 'Regulated 38.5 - 40 hour work weeks with up to 30 days of paid annual vacation.' },
                                { icon: Globe, title: 'Family Reunification', desc: 'Clear pathways to bring your spouse and dependents to Germany.' },
                                { icon: Stethoscope, title: 'Career Growth', desc: 'Opportunities for specialization (ICU, Anesthesia, Oncology) funded by employers.' },
                                { icon: CheckCircle, title: 'Permanent Residency', desc: 'Eligibility for German Permanent Settlement Permit (Niederlassungserlaubnis) and Citizenship.' },
                            ].map((benefit, i) => (
                                <div key={i} className="text-center bg-slate-800/50 p-6 rounded-2xl border border-slate-700">
                                    <benefit.icon className="w-12 h-12 text-red-400 mx-auto mb-4" />
                                    <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
                                    <p className="text-slate-400">{benefit.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* How We Help */}
                <section className="py-16">
                    <div className="container mx-auto px-4 max-w-4xl">
                        <h2 className="text-3xl md:text-5xl font-black text-white text-center mb-12">
                            Our Support Process
                        </h2>
                        <div className="space-y-6">
                            {[
                                { step: '1', title: 'Language Training', desc: 'We connect you with certified institutes to achieve B1/B2 German proficiency.' },
                                { step: '2', title: 'Anerkennung (Recognition)', desc: 'Full assistance in compiling and submitting documents to German health authorities for credential evaluation.' },
                                { step: '3', title: 'Employer Matching', desc: 'Direct interviews with leading German hospitals, clinics, and elderly care facilities.' },
                                { step: '4', title: 'Visa & Relocation', desc: 'End-to-end support for your work visa application, flight booking, and finding accommodation in Germany.' },
                                { step: '5', title: 'Post-Arrival Support', desc: 'Guidance on registration, opening a bank account, and enrolling in the required adaptation course.' }
                            ].map((item, i) => (
                                <div key={i} className="flex gap-6 bg-slate-800/30 p-6 rounded-2xl border border-slate-700/50">
                                    <div className="w-12 h-12 shrink-0 bg-red-500/20 text-red-400 rounded-full flex items-center justify-center font-bold text-xl">
                                        {item.step}
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                                        <p className="text-slate-400">{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="py-16">
                    <div className="container mx-auto px-4">
                        <div className="max-w-3xl mx-auto text-center bg-gradient-to-r from-red-500/10 to-pink-500/10 border border-red-500/30 rounded-3xl p-12">
                            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
                                Ready to Start Your Nursing Career in Germany?
                            </h2>
                            <p className="text-slate-300 mb-8">
                                Join hundreds of healthcare professionals who have successfully built their careers in Germany with our support.
                            </p>
                            <Link
                                to="/signup"
                                className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-pink-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-[0_0_30px_rgba(239,68,68,0.5)] transition-all"
                            >
                                Apply Now
                                <ArrowRight className="w-5 h-5" />
                            </Link>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
};

export default Healthcare;

